<script setup lang="ts">
import {ConversationMessage} from "@/conversation/interfaces";
import {CustomerSummary} from "@/customer/interfaces/Customer";
import {computed, ref} from "vue";
import formatHelper from "@/core/helpers/formatHelper";
import {isRCSOutbound} from "@/conversation/interfaces/guard";
import ConversationMessageStatusBadge from "@/conversation/components/ConversationMessageStatusBadge.vue";
import {Button} from "@/core/components/ui/button";
import {ChevronRight, MessageCircle, MessageSquare, MousePointerClick} from "@lucide/vue";
import ConversationMessagePreviewTooltip from "@/conversation/components/ConversationMessagePreviewTooltip.vue";
import MessageInteractions from "@/conversation/components/MessageInteractions.vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import CustomerConversationMessagesDrawer from "@/conversation/components/CustomerConversationMessagesDrawer.vue";
import ConversationMessageFlowThread from "@/conversation/components/ConversationMessageFlowThread.vue";
import ConversationMessageTitle from "@/conversation/components/ConversationMessageTitle.vue";

const props = defineProps<{
    messages: ConversationMessage[];
    total: number;
    loading?: boolean;
    extends?: ('sent_at' | 'preview' | 'flow' | 'title')[]
}>();

const emit = defineEmits<{
    'update:page': [page: number];
    'update:items-per-page': [limit: number];
}>();

const limits = defineModel<number>("limits", {default: 20});

const selectedCustomer = ref<CustomerSummary>();
const drawerOpen = ref(false);

const openDrawer = (customer?: CustomerSummary) => {
    if (!customer) {
        return;
    }

    selectedCustomer.value = customer;
    drawerOpen.value = true;
};

const withSentAt = props.extends?.includes('sent_at') ?? false;
const withFlow = props.extends?.includes('flow') ?? false;

const headers = [
    {title: 'Patient', value: 'customer', nowrap: true},
    ...(props.extends?.includes('title') ? [{title: 'Contexte', value: 'title'}] : []),
    {title: withSentAt ? 'Envoyé le' : 'Statut', value: 'sent_at', nowrap: true},
    {title: '', value: 'open', sortable: false, align: 'end' as const, width: '1%', nowrap: true},
];

/**
 * Count the patient replies to a message — for a flow, over the whole outbound chain,
 * so the badge matches the thread shown when expanding the row.
 */
const repliesCount = (message: ConversationMessage): number => {
    const chain = message.flow_messages?.length ? message.flow_messages : [message];

    return chain.reduce((count, step) => count + (step.replies_count ?? step.replies?.length ?? 0), 0);
};

/**
 * Count the patient clicks on a message (RCS button interactions).
 */
const clicksCount = (message: ConversationMessage): number => {
    if (!isRCSOutbound(message)) {
        return 0;
    }

    return message.interactions_count ?? message.interactions?.length ?? 0;
};

const items = computed(() => {
    return props.messages.map((message) => {
        return {
            id: message.id,
            customer: message.customer,
            channel: message.channel,
            status: message.status,
            flow_messages: message.flow_messages ?? [],
            rcs: isRCSOutbound(message) ? message : undefined,
            replies_count: repliesCount(message),
            clicks_count: clicksCount(message),
            has_failover: message.has_failover,
            sent_at: message.sent_at ? new Date(message.sent_at) : undefined,
            raw: message
        };
    });
});

const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'});
};
</script>

<template>
  <v-data-table-server
    v-model:items-per-page="limits"
    :headers="headers"
    :items="items"
    :items-length="total"
    :loading="loading"
    :items-per-page-options="[10, 20, 50]"
    item-value="id"
    @update:page="emit('update:page', $event)"
  >
    <template #[`item.customer`]="{item}">
      <div class="flex gap-2 items-center">
        <img
          v-if="item.customer?.onboarded" alt="Certifié" src="@/assets/icons/certified.svg"
          class="shrink-0 w-10 object-contain"
        >
        <div>
          <p class="font-medium">
            {{ item.customer?.information?.name ?? 'Patient inconnu' }}
          </p>
          <span v-if="item.customer?.information?.phone" class="text-muted-foreground">
            {{ formatHelper.readablePhone(item.customer?.information?.phone) }}
          </span>
        </div>
      </div>
    </template>
    <template #[`item.title`]="{item}">
      <ConversationMessageTitle :message="item.raw" />
    </template>
    <template #[`item.sent_at`]="{item, internalItem, isExpanded, toggleExpand}">
      <div class="flex flex-col items-start gap-1 py-2">
        <template v-if="withSentAt">
          <div v-if="item.sent_at" class="flex items-baseline gap-1.5 whitespace-nowrap">
            <span class="text-sm font-medium">{{ item.sent_at.toLocaleDateString('fr-FR') }}</span>
            <span class="text-xs text-muted-foreground">{{ formatTime(item.sent_at) }}</span>
          </div>
          <span v-else class="text-sm text-muted-foreground">—</span>
        </template>
        <button
          v-if="item.replies_count"
          type="button"
          class="flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium !text-green-700"
          :class="withFlow ? 'cursor-pointer hover:bg-green-100' : 'cursor-default'"
          :title="withFlow ? 'Voir le cheminement du flux' : undefined"
          @click.stop="withFlow && toggleExpand(internalItem)"
        >
          <MessageSquare class="w-3.5 h-3.5 shrink-0" />
          <span class="leading-none">{{ item.replies_count }} réponse{{ item.replies_count > 1 ? 's' : '' }}</span>
          <ChevronRight
            v-if="withFlow"
            class="w-3.5 h-3.5 shrink-0 transition-transform"
            :class="isExpanded(internalItem) ? 'rotate-90' : ''"
          />
        </button>
        <TooltipProvider v-else-if="item.clicks_count && item.rcs" :delay-duration="200">
          <Tooltip>
            <TooltipTrigger as-child class="cursor-pointer" @click.stop>
              <span
                class="flex items-center gap-1 rounded-full border border-green-300 px-2.5 py-0.5 text-xs font-medium text-green-700"
              >
                <MousePointerClick class="w-3.5 h-3.5 shrink-0" />
                {{ item.clicks_count }} clic{{ item.clicks_count > 1 ? 's' : '' }}
                <ChevronRight class="w-3.5 h-3.5 shrink-0" />
              </span>
            </TooltipTrigger>
            <TooltipContent class="bg-white max-w-sm shadow-2xl" avoid-collisions>
              <MessageInteractions :interactions="item.rcs.interactions ?? []" :payload="item.rcs.payload" />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <ConversationMessageStatusBadge
          v-else
          :status="item.status"
          :has-failover="item.has_failover"
          :channel="item.channel"
        />
      </div>
    </template>
    <template #expanded-row="{columns, item}">
      <tr>
        <td :colspan="columns.length" class="bg-slate-50 px-6">
          <ConversationMessageFlowThread :messages="item.flow_messages.length ? item.flow_messages : [item.raw]" />
        </td>
      </tr>
    </template>
    <template #[`item.open`]="{item}">
      <div class="flex items-center justify-end gap-3">
        <ConversationMessagePreviewTooltip v-if="props.extends?.includes('preview')" :message="item.raw" />
        <Button
          variant="outline"
          size="sm"
          class="rounded-full gap-2 text-primary border-primary/40 hover:text-primary"
          title="Voir la conversation"
          @click.stop="openDrawer(item.customer)"
        >
          <MessageCircle class="w-4 h-4 shrink-0" />
          <span class="hidden md:inline font-medium">Conversation</span>
        </Button>
      </div>
    </template>
  </v-data-table-server>

  <CustomerConversationMessagesDrawer v-model:open="drawerOpen" :customer="selectedCustomer" />
</template>

<style scoped>

</style>
